(() => {
  const page = document.querySelector('[data-nyanmizu-pdp]');
  if (!page || page.dataset.initialized === 'true') return;
  page.dataset.initialized = 'true';

  const galleryStage = page.querySelector('.nm-gallery__stage');
  const galleryMain = page.querySelector('[data-gallery-main]');
  const galleryThumbs = [...page.querySelectorAll('[data-gallery-thumb]')];
  let galleryRequestId = 0;

  const updateActiveThumbnail = (selectedThumb, scrollIntoView = false) => {
    galleryThumbs.forEach((item) => {
      const selected = item === selectedThumb;
      item.classList.toggle('is-active', selected);
      item.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    if (scrollIntoView) {
      selectedThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  const preloadGalleryImage = async (src) => {
    const image = new Image();
    const loaded = new Promise((resolve, reject) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', reject, { once: true });
    });
    image.src = src;

    if (typeof image.decode === 'function') {
      try {
        await image.decode();
        return;
      } catch (error) {
        await loaded;
        return;
      }
    }

    await loaded;
  };

  const selectGalleryImage = async (thumb, scrollIntoView = false) => {
    if (!galleryMain || !thumb?.dataset.src) return;

    const nextSrc = new URL(thumb.dataset.src, document.baseURI).href;
    const requestId = ++galleryRequestId;
    updateActiveThumbnail(thumb, scrollIntoView);

    if (galleryMain.src === nextSrc) {
      galleryMain.alt = thumb.dataset.alt || '';
      return;
    }

    try {
      await preloadGalleryImage(nextSrc);
    } catch (error) {
      return;
    }

    if (requestId !== galleryRequestId) return;
    galleryMain.src = nextSrc;
    galleryMain.alt = thumb.dataset.alt || '';
  };

  galleryThumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      selectGalleryImage(thumb);
    });
  });

  if (galleryStage && galleryThumbs.length > 1) {
    let swipePointerId = null;
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swipeLastX = 0;
    let swipeLastY = 0;

    const resetSwipe = () => {
      swipePointerId = null;
    };

    galleryStage.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      swipePointerId = event.pointerId;
      swipeStartX = event.clientX;
      swipeStartY = event.clientY;
      swipeLastX = event.clientX;
      swipeLastY = event.clientY;
      galleryStage.setPointerCapture?.(event.pointerId);
    });

    galleryStage.addEventListener('pointermove', (event) => {
      if (event.pointerId !== swipePointerId) return;
      swipeLastX = event.clientX;
      swipeLastY = event.clientY;
    });

    galleryStage.addEventListener('pointerup', (event) => {
      if (event.pointerId !== swipePointerId) return;
      swipeLastX = event.clientX;
      swipeLastY = event.clientY;

      const distanceX = swipeLastX - swipeStartX;
      const distanceY = swipeLastY - swipeStartY;
      const activeIndex = galleryThumbs.findIndex((thumb) => thumb.classList.contains('is-active'));

      if (Math.abs(distanceX) >= 45 && Math.abs(distanceX) > Math.abs(distanceY) * 1.25) {
        const nextIndex = distanceX < 0
          ? Math.min(activeIndex + 1, galleryThumbs.length - 1)
          : Math.max(activeIndex - 1, 0);

        if (nextIndex !== activeIndex) {
          selectGalleryImage(galleryThumbs[nextIndex], true);
        }
      }

      resetSwipe();
    });

    galleryStage.addEventListener('pointercancel', resetSwipe);
    galleryStage.addEventListener('dragstart', (event) => event.preventDefault());
  }

  const variantInput = page.querySelector('[data-variant-input]');
  const bundleButtons = [...page.querySelectorAll('[data-bundle]')];

  bundleButtons.forEach((bundle) => {
    bundle.addEventListener('click', () => {
      bundleButtons.forEach((item) => {
        const selected = item === bundle;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      if (variantInput && bundle.dataset.variantId) {
        variantInput.value = bundle.dataset.variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });

  const ugcVideoCarousel = page.querySelector('[data-ugc-video-carousel]');

  if (ugcVideoCarousel) {
    ugcVideoCarousel.querySelectorAll('[data-ugc-video]').forEach((video) => {
      video.controls = false;
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });

    let videoDragging = false;
    let videoDragMoved = false;
    let videoDragStartX = 0;
    let videoDragStartScrollLeft = 0;

    const stopVideoDragging = (event) => {
      if (!videoDragging) return;
      videoDragging = false;
      ugcVideoCarousel.classList.remove('is-dragging');
      if (event?.pointerId !== undefined) {
        try {
          ugcVideoCarousel.releasePointerCapture?.(event.pointerId);
        } catch (error) {
          // The browser may already have released this pointer.
        }
      }
    };

    ugcVideoCarousel.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (ugcVideoCarousel.scrollWidth <= ugcVideoCarousel.clientWidth) return;

      videoDragging = true;
      videoDragMoved = false;
      videoDragStartX = event.clientX;
      videoDragStartScrollLeft = ugcVideoCarousel.scrollLeft;
      ugcVideoCarousel.classList.add('is-dragging');
      ugcVideoCarousel.setPointerCapture?.(event.pointerId);
    });

    ugcVideoCarousel.addEventListener('pointermove', (event) => {
      if (!videoDragging) return;
      const distanceX = event.clientX - videoDragStartX;
      if (Math.abs(distanceX) > 4) videoDragMoved = true;
      ugcVideoCarousel.scrollLeft = videoDragStartScrollLeft - distanceX;
      event.preventDefault();
    });

    ugcVideoCarousel.addEventListener('pointerup', stopVideoDragging);
    ugcVideoCarousel.addEventListener('pointercancel', stopVideoDragging);
    ugcVideoCarousel.addEventListener('lostpointercapture', stopVideoDragging);
    ugcVideoCarousel.addEventListener('click', (event) => {
      if (!videoDragMoved) return;
      event.preventDefault();
      event.stopPropagation();
      videoDragMoved = false;
    }, true);
  }

  const reviewItems = [...page.querySelectorAll('[data-review-item]')];
  const reviewPagination = page.querySelector('[data-review-pagination]');

  if (reviewPagination && reviewItems.length > 1) {
    const pageButtons = [...reviewPagination.querySelectorAll('[data-review-page]')];
    const previousButton = reviewPagination.querySelector('[data-review-previous]');
    const nextButton = reviewPagination.querySelector('[data-review-next]');
    const reviewsPerPage = Number(reviewPagination.dataset.reviewsPerPage) || 3;
    const pageCount = Math.ceil(reviewItems.length / reviewsPerPage);
    let currentPage = 1;

    const showReviewPage = (pageNumber) => {
      currentPage = Math.min(Math.max(pageNumber, 1), pageCount);

      const firstReviewIndex = (currentPage - 1) * reviewsPerPage;
      const lastReviewIndex = firstReviewIndex + reviewsPerPage;

      reviewItems.forEach((review, index) => {
        review.hidden = index < firstReviewIndex || index >= lastReviewIndex;
      });

      pageButtons.forEach((button) => {
        const isCurrent = Number(button.dataset.reviewPage) === currentPage;
        if (isCurrent) {
          button.setAttribute('aria-current', 'page');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      previousButton.disabled = currentPage === 1;
      nextButton.disabled = currentPage === pageCount;
    };

    pageButtons.forEach((button) => {
      button.addEventListener('click', () => {
        showReviewPage(Number(button.dataset.reviewPage));
      });
    });

    previousButton.addEventListener('click', () => showReviewPage(currentPage - 1));
    nextButton.addEventListener('click', () => showReviewPage(currentPage + 1));

    reviewPagination.hidden = false;
    showReviewPage(1);
  }
})();
